import { Box } from "@mui/material";
import NextLink from "next/link";
import ArticleCoverImage from "@/src/components/journal-site/common/article/article-cover-image/article-cover-image";
import ArticleAuthorsList from "@/src/components/journal-site/common/article/article-authors-list/article-authors-list";
import DownloadBox from "@/src/components/journal-site/common/article/mui-custom/article-information/download-box";
import ArticleCite from "@/src/components/journal-site/common/article/article-cite/article-cite";
import EntryTitle from "@/src/components/common/broadsheet/entry-title";
import Kicker from "@/src/components/common/broadsheet/kicker";
import MetaLine from "@/src/components/common/broadsheet/meta-line";
import {
  FONT_DISPLAY,
  FONT_UI,
} from "@/src/components/common/broadsheet/tokens";
import { PROOF_FINAL_PUBLISHED } from "@/src/constant/constant";
import ReactHtmlParser from "@/src/utils/html-parser";
import { getDoiLink } from "@/src/utils/journal-services";
import { resolveArticleThumbnailSrc } from "@/src/utils/paths";
import ArticleProps from "@/src/_types/Article/article-props";
import { withAiDisclosure } from "@/src/utils/image-credit";

const dateStackSx = {
  fontFamily: FONT_UI,
  fontSize: "0.6875rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  lineHeight: 1.4,
  color: "custom.subtitleText",
};

const Article = (props: ArticleProps) => {
  let epubChecker = false;
  let day: string | undefined;
  let month: string | undefined;
  let year: string | undefined;

  if (props.article.pub_dates?.epub) {
    epubChecker = true;

    const date: Date = new Date(props.article.pub_dates.epub);

    day = date.toLocaleDateString("en-US", {
      day: "numeric",
    });
    month = date.toLocaleDateString("en-US", {
      month: "short",
    });
    year = date.toLocaleDateString("en-US", {
      year: "numeric",
    });
  }

  const renderCoverImage = () => {
    if (props.article.feature_image_thumbnail?.content) {
      return (
        <ArticleCoverImage
          src={
            resolveArticleThumbnailSrc(props.article) ??
            props.article.feature_image_thumbnail.content
          }
          caption={withAiDisclosure(
            props.article.title ?? "",
            props.article.feature_image_thumbnail,
          )}
          spine={props.article.spine}
        />
      );
    } else if (props.article.issue_featured_image) {
      return (
        <ArticleCoverImage
          src={
            process.env.NEXT_PUBLIC_SERVICE_URI_CDN_SERVE +
            props.article.issue_featured_image
          }
          caption={props.article.title ?? ""}
          spine={props.article.spine}
        />
      );
    } else {
      return (
        <ArticleCoverImage
          src={""}
          caption={props.article.title ?? ""}
          spine={props.article.spine}
        />
      );
    }
  };

  const scheduledNote =
    props.article.proof_status !== PROOF_FINAL_PUBLISHED &&
    Boolean(props.article.desired_issue_id) &&
    props.article.issue_id !== props.article.desired_issue_id
      ? `${props.article.proof_status} scheduled for ${
          props.article.desired_issue?.volume_name
        } (${props.article.desired_issue?.name})`
      : null;

  return (
    <Box
      className="bf-ui"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          width: { xs: "100%", md: "auto" },
          marginBottom: { sm: "1.5rem", lg: 0 },
          position: "relative",
        }}
      >
        {epubChecker && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              alignSelf: "center",
              p: 0.5,
            }}
          >
            <Box
              sx={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 700,
                fontSize: "1.25rem",
                lineHeight: 1,
                color: "custom.ink",
              }}
            >
              {day}
            </Box>
            <Box sx={dateStackSx}>{month}</Box>
            <Box sx={dateStackSx}>{year}</Box>
          </Box>
        )}
        {renderCoverImage()}
        {props.article.has_altmetric && (
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              px: 0.3125,
              py: 0.125,
              backgroundColor: "custom.ink",
              color: "custom.newsprint",
              fontFamily: FONT_UI,
              fontSize: "0.625rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              zIndex: 1,
            }}
          >
            Altmetric
          </Box>
        )}
      </Box>

      <Box
        sx={{
          paddingX: { lg: 1.5, xs: 0.75 },
          minWidth: 0,
        }}
      >
        {props.article.document_type && (
          <Kicker component="span">{props.article.document_type}</Kicker>
        )}

        <EntryTitle
          href={props.article.href || "#"}
          component="h3"
          size="lg"
          sx={{ mt: 0.25, mb: 0.375 }}
        >
          {ReactHtmlParser(props.article.title)}
          {ReactHtmlParser(
            props.article.document_flags
              ? ` <span class="flag">${props.article.document_flags}</span>`
              : "",
          )}
        </EntryTitle>

        <Box
          sx={{
            fontFamily: FONT_UI,
            fontSize: "0.8125rem",
            lineHeight: 1.5,
            color: "custom.inkMuted",
            mb: 0.375,
          }}
        >
          <ArticleAuthorsList authors={props.article.authors} />
        </Box>

        <MetaLine
          items={[
            props.article.pdf_file ? (
              <DownloadBox key="pdf" href={props.article.pdf_file} />
            ) : null,
            props.article.proof_status,
            props.article.proof_status === PROOF_FINAL_PUBLISHED
              ? null
              : scheduledNote,
            props.article.publon_badge && props.article.doi ? (
              <Box
                key="doi"
                component={NextLink}
                target="_blank"
                href={getDoiLink(props.article.doi)}
                data-doi={getDoiLink(props.article.doi)}
                sx={{ color: "custom.link", textDecoration: "none" }}
              >
                {getDoiLink(props.article.doi)}
              </Box>
            ) : null,
            ...(props.meta ?? []),
          ]}
        />

        {props.showHowToCite && props.article.how_to_cite && (
          <ArticleCite howToCite={props.article.how_to_cite} />
        )}
      </Box>
    </Box>
  );
};

export default Article;
